/**
 * COL-220: Super-admin chain reassignment dialog.
 *
 * Flow:
 *   1. Admin picks a target room for the current tenant.
 *   2. If the target is vacant → move is "resolved", commit unlocks.
 *   3. If the target is occupied → that occupant is displaced; the
 *      dialog pivots to picking THEIR new room.
 *   4. Repeat until the head of the queue has a vacant target.
 *   5. Commit applies the whole chain atomically via the RPC.
 *
 * Invariants enforced in the UI (the RPC re-checks at commit):
 *   - Can't pick a tenant's current room (no-op).
 *   - Can't pick a room already "earmarked" by an earlier move in the
 *     chain (would create a cycle / double-occupancy).
 *   - Can't commit while a tenant in the chain still needs a room.
 *   - Cancel = abandon, no DB writes.
 *
 * Scope: rooms across the entire portfolio are eligible — the ticket
 * (COL-220) asks for "change the room assigned to a tenant" without
 * a property qualifier, and admins routinely need to correct
 * wrong-listing applications between properties. The property filter
 * defaults to the tenant's current property (same-property reassign
 * is still the common case), with "All properties" available for the
 * cross-property scenario.
 */
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Check, Loader2, Users, X } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { useLanguage } from "@/contexts/LanguageContext";
import { cn } from "@/lib/utils";
import { useProperties } from "@/hooks/v2/useProperties";
import {
  useAllActiveOccupants,
  useAllRooms,
  useReassignBookings,
  type ActiveOccupant,
  type ReassignMove,
} from "@/hooks/v2/useReassignBookings";

/** Minimal room shape pulled by useAllRooms — narrower than the full
 *  Room type from src/types/property, so we declare it locally. */
type ReassignRoom = {
  id: number;
  property_id: number;
  title: string;
  price: number | null;
  is_hidden: boolean;
};

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The tenant initiating the move. */
  initialBookingId: number;
  initialTenantName: string;
  initialFromRoomId: number;
  /** Property the tenant currently lives in. Used as the initial value
   *  of the property filter — not a hard scope. Admin can switch to
   *  "All properties" or any other property from inside the dialog. */
  propertyId: number;
};

/** A single hop in the chain that already has a target selected. */
type ResolvedMove = {
  bookingId: number;
  tenantName: string;
  fromRoomId: number;
  toRoomId: number;
};

/** The head of the queue — a tenant who still needs a target room. */
type PendingTenant = {
  bookingId: number;
  tenantName: string;
  fromRoomId: number;
};

function nameForOccupant(
  o: ActiveOccupant,
  t: (key: string, fallback: string) => string
): string {
  return (
    o.name?.trim() ||
    o.email?.trim() ||
    `${t("admin.reassign.tenantHash", "Tenant #")}${o.bookingId}`
  );
}

/** "870,00 €/mo" — the room's base rent, which becomes the tenant's on a move. */
function formatRentPerMonth(price: number): string {
  return `${new Intl.NumberFormat("de-DE", {
    style: "currency",
    currency: "EUR",
  }).format(price)}/mo`;
}

export function ReassignRoomDialog({
  open,
  onOpenChange,
  initialBookingId,
  initialTenantName,
  initialFromRoomId,
  propertyId,
}: Props) {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const { data: rawRooms = [], isLoading: roomsLoading } = useAllRooms();
  const { data: occupants = new Map(), isLoading: occupantsLoading } =
    useAllActiveOccupants();
  const { data: propertyList = [] } = useProperties();
  const commitMut = useReassignBookings();

  // Drop hidden rooms — admins shouldn't be able to reassign anyone
  // into an archived/decommissioned room from this dialog.
  const rooms = useMemo(
    () => (rawRooms as ReassignRoom[]).filter((r) => !r.is_hidden),
    [rawRooms]
  );

  // room id → room, for the post-commit "View {new room}" toast jump.
  const roomById = useMemo(() => {
    const m = new Map<number, ReassignRoom>();
    for (const r of rooms) m.set(r.id, r);
    return m;
  }, [rooms]);

  // property_id → human title, for room card sublabels + the filter
  // dropdown. Property may be deleted while a stale booking still
  // references it; fall back to "Property #<id>" in that case.
  const propertyTitleById = useMemo(() => {
    const m = new Map<number, string>();
    for (const p of propertyList) m.set(Number(p.id), p.title);
    return m;
  }, [propertyList]);

  // Property filter — defaults to the tenant's current property so the
  // same-property reassign workflow is preserved as the default. "all"
  // shows every room across every property.
  const [propertyFilter, setPropertyFilter] = useState<number | "all">(
    propertyId
  );
  useEffect(() => {
    if (open) setPropertyFilter(propertyId);
  }, [open, propertyId]);

  // Displacement opt-in — by default, picking an occupied room is
  // disabled so admins don't accidentally start a chain by clicking
  // through. Flipping this on unlocks occupied rooms; the chain pivot
  // behaviour is unchanged from there on.
  const [allowDisplacement, setAllowDisplacement] = useState(false);
  useEffect(() => {
    if (open) setAllowDisplacement(false);
  }, [open]);

  const [resolved, setResolved] = useState<ResolvedMove[]>([]);
  const [pending, setPending] = useState<PendingTenant>({
    bookingId: initialBookingId,
    tenantName: initialTenantName,
    fromRoomId: initialFromRoomId,
  });

  // Reset whenever the dialog opens for a fresh tenant.
  useEffect(() => {
    if (open) {
      setResolved([]);
      setPending({
        bookingId: initialBookingId,
        tenantName: initialTenantName,
        fromRoomId: initialFromRoomId,
      });
    }
  }, [open, initialBookingId, initialTenantName, initialFromRoomId]);

  // Rooms already claimed as a destination by a resolved move. Picking
  // one of these again would create a cycle, so we disable them.
  const earmarkedTargets = useMemo(
    () => new Set(resolved.map((m) => m.toRoomId)),
    [resolved]
  );

  // Bookings already displaced once — we don't ask the same tenant
  // twice in a single chain (their reassign was handled).
  const displacedBookings = useMemo(
    () => new Set(resolved.map((m) => m.bookingId)),
    [resolved]
  );

  const isLoading = roomsLoading || occupantsLoading;

  function pickRoom(targetRoom: ReassignRoom) {
    if (targetRoom.id === pending.fromRoomId) {
      toast.error(
        t(
          "admin.reassign.error.alreadyCurrentRoom",
          "That's already their current room."
        )
      );
      return;
    }
    if (earmarkedTargets.has(targetRoom.id)) {
      toast.error(
        t(
          "admin.reassign.error.alreadyDestination",
          "That room is already the destination of an earlier move."
        )
      );
      return;
    }

    // Detect displacement: is there an active occupant on the target
    // room that we haven't already displaced in this chain?
    const occupant = occupants.get(targetRoom.id) ?? null;
    const isFreshDisplacement =
      occupant != null && !displacedBookings.has(occupant.bookingId);

    const newResolved: ResolvedMove = {
      bookingId: pending.bookingId,
      tenantName: pending.tenantName,
      fromRoomId: pending.fromRoomId,
      toRoomId: targetRoom.id,
    };

    setResolved((prev) => [...prev, newResolved]);

    if (isFreshDisplacement && occupant) {
      setPending({
        bookingId: occupant.bookingId,
        tenantName: nameForOccupant(occupant, t),
        fromRoomId: targetRoom.id,
      });
    } else {
      // Chain complete — no further displacement to resolve.
      setPending(null as unknown as PendingTenant);
    }
  }

  function undoLastStep() {
    setResolved((prev) => {
      if (!prev.length) return prev;
      const last = prev[prev.length - 1];
      // Restore the just-removed move's tenant as the head of the queue
      // so the admin can pick a different room.
      setPending({
        bookingId: last.bookingId,
        tenantName: last.tenantName,
        fromRoomId: last.fromRoomId,
      });
      return prev.slice(0, -1);
    });
  }

  const chainComplete = pending === null || (pending as unknown) === null;
  const canCommit = chainComplete && resolved.length > 0;

  function handleCommit() {
    if (!canCommit) return;
    const moves: ReassignMove[] = resolved.map((m) => ({
      booking_id: m.bookingId,
      new_room_id: m.toRoomId,
    }));
    // The first hop is the tenant this dialog opened for — offer a jump to
    // their new room so the admin can follow the move.
    const primary = resolved[0];
    const primaryTargetRoom = primary
      ? roomById.get(primary.toRoomId)
      : undefined;
    commitMut.mutate(moves, {
      onSuccess: () => {
        toast.success(
          resolved.length === 1
            ? t("admin.reassign.success.single", "Room reassigned.")
            : t(
                "admin.reassign.success.multiple",
                "{count} bookings reassigned."
              ).replace("{count}", String(resolved.length)),
          primaryTargetRoom
            ? {
                action: {
                  label: `${t("admin.reassign.viewPrefix", "View")} ${primaryTargetRoom.title}`,
                  onClick: () =>
                    navigate(
                      `/admin/properties/${primaryTargetRoom.property_id}/rooms/${primaryTargetRoom.id}`
                    ),
                },
              }
            : undefined
        );
        onOpenChange(false);
      },
      onError: (err) => {
        toast.error(
          err instanceof Error
            ? err.message
            : t(
                "admin.reassign.error.commitFailed",
                "Could not commit reassignment — try again."
              )
        );
      },
    });
  }

  function handleClose(nextOpen: boolean) {
    // Prevent closing mid-chain unless commit/cancel is intentional.
    // The Cancel button below uses onOpenChange(false) explicitly; the
    // backdrop / Esc go through this guard too — we just confirm there's
    // no pending work and warn the admin.
    if (!nextOpen && resolved.length > 0 && !commitMut.isPending) {
      const ok = window.confirm(
        t(
          "admin.reassign.confirmDiscard",
          "You have pending reassignments that haven't been saved. Discard them?"
        )
      );
      if (!ok) return;
    }
    onOpenChange(nextOpen);
  }

  const rowsByOccupancy = useMemo(() => {
    const filtered =
      propertyFilter === "all"
        ? rooms
        : rooms.filter((r) => r.property_id === propertyFilter);
    return filtered.map((r) => {
      const occ = occupants.get(r.id) ?? null;
      const occDisplacedAlready = occ
        ? displacedBookings.has(occ.bookingId)
        : false;
      return {
        room: r,
        occupant: occ,
        // After the admin already moved this room's old tenant out,
        // it counts as vacant for the rest of the chain.
        treatAsVacant: !occ || occDisplacedAlready,
        earmarked: earmarkedTargets.has(r.id),
        isCurrent: r.id === pending?.fromRoomId,
        propertyTitle:
          propertyTitleById.get(r.property_id) ??
          `${t("admin.reassign.propertyHash", "Property #")}${r.property_id}`,
      };
    });
  }, [
    rooms,
    occupants,
    displacedBookings,
    earmarkedTargets,
    pending,
    propertyFilter,
    propertyTitleById,
    t,
  ]);

  // Distinct properties the rooms span, for the filter dropdown. Sorted
  // alphabetically with the tenant's current property pinned first so
  // it's easy to find.
  const filterOptions = useMemo(() => {
    const ids = Array.from(new Set(rooms.map((r) => r.property_id)));
    const sorted = ids.sort((a, b) => {
      if (a === propertyId) return -1;
      if (b === propertyId) return 1;
      const ta = propertyTitleById.get(a) ?? "";
      const tb = propertyTitleById.get(b) ?? "";
      return ta.localeCompare(tb);
    });
    return sorted.map((id) => ({
      id,
      title:
        propertyTitleById.get(id) ??
        `${t("admin.reassign.propertyHash", "Property #")}${id}`,
    }));
  }, [rooms, propertyId, propertyTitleById, t]);

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {chainComplete
              ? t("admin.reassign.reviewTitle", "Review reassignment")
              : `${t("admin.reassign.whereShouldGoPrefix", "Where should")} ${pending.tenantName} ${t("admin.reassign.whereShouldGoSuffix", "go?")}`}
          </DialogTitle>
          <DialogDescription>
            {chainComplete
              ? `${resolved.length} ${
                  resolved.length === 1
                    ? t("admin.reassign.moveSingular", "move")
                    : t("admin.reassign.movesPlural", "moves")
                } ${t("admin.reassign.readyCommit", "ready. Commit applies them in a single transaction.")}`
              : t(
                  "admin.reassign.pickTargetRoom",
                  "Pick a target room. If it's occupied, you'll be prompted to reassign that tenant next."
                )}{" "}
            {t(
              "admin.reassign.rentDepositNote",
              "Rent and deposit follow the room: each moved tenant's base rent, deposit and unpaid invoices are re-priced to their new room. Paid invoices and an already-paid deposit are left as they are."
            )}
          </DialogDescription>
        </DialogHeader>

        {/* Chain so far */}
        {resolved.length > 0 && (
          <Card className="p-4 space-y-2 bg-muted/30">
            <header className="flex items-center justify-between">
              <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                {t("admin.reassign.pendingChain", "Pending chain")} (
                {resolved.length})
              </h4>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={undoLastStep}
                disabled={commitMut.isPending}
              >
                {t("admin.reassign.undoLast", "Undo last")}
              </Button>
            </header>
            <ul className="space-y-1.5 text-sm">
              {resolved.map((m, i) => {
                const fromRoom = rooms.find((r) => r.id === m.fromRoomId);
                const toRoom = rooms.find((r) => r.id === m.toRoomId);
                return (
                  <li
                    key={`${m.bookingId}-${i}`}
                    className="flex items-center gap-2 flex-wrap"
                  >
                    <span className="font-medium">{m.tenantName}</span>
                    <span className="text-muted-foreground">
                      {fromRoom?.title ??
                        `${t("admin.reassign.roomHash", "Room #")}${m.fromRoomId}`}
                    </span>
                    <ArrowRight className="h-3.5 w-3.5 text-muted-foreground" />
                    <span className="text-foreground">
                      {toRoom?.title ??
                        `${t("admin.reassign.roomHash", "Room #")}${m.toRoomId}`}
                    </span>
                    {toRoom?.price != null && (
                      <span className="text-xs text-muted-foreground">
                        · {t("admin.reassign.rentLower", "rent")}{" "}
                        {fromRoom?.price != null &&
                        fromRoom.price !== toRoom.price ? (
                          <>
                            <span className="line-through">
                              {formatRentPerMonth(fromRoom.price)}
                            </span>{" "}
                            <span className="font-medium text-foreground">
                              {formatRentPerMonth(toRoom.price)}
                            </span>
                          </>
                        ) : (
                          formatRentPerMonth(toRoom.price)
                        )}
                      </span>
                    )}
                  </li>
                );
              })}
            </ul>
          </Card>
        )}

        {/* Room picker (only when there's still a tenant to assign) */}
        {!chainComplete && (
          <div className="space-y-2">
            <div className="text-xs text-muted-foreground">
              {t("admin.reassign.reassigningPrefix", "Reassigning")}{" "}
              <strong>{pending.tenantName}</strong>{" "}
              {t("admin.reassign.fromLower", "from")}{" "}
              <strong>
                {rooms.find((r) => r.id === pending.fromRoomId)?.title ??
                  `${t("admin.reassign.roomHash", "Room #")}${pending.fromRoomId}`}
              </strong>
              .
            </div>

            {/* Property filter + displacement opt-in.
                - Property filter defaults to the tenant's current
                  property; "All properties" enables cross-portfolio
                  reassign per COL-220 scope.
                - Allow displacement is OFF by default so admins don't
                  accidentally trigger a chain by clicking through an
                  occupied row. Flip it on to unlock occupied rooms;
                  the chain pivot behaviour is unchanged from there on. */}
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between sm:flex-wrap">
              <div className="flex items-center gap-2 flex-wrap">
                <label className="text-xs text-muted-foreground">
                  {t("admin.reassign.showRoomsIn", "Show rooms in")}
                </label>
                <Select
                  value={
                    propertyFilter === "all" ? "all" : String(propertyFilter)
                  }
                  onValueChange={(v) =>
                    setPropertyFilter(v === "all" ? "all" : Number(v))
                  }
                >
                  <SelectTrigger className="h-8 w-auto min-w-[14rem] text-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">
                      {t("admin.reassign.allProperties", "All properties")}
                    </SelectItem>
                    {filterOptions.map((p) => (
                      <SelectItem key={p.id} value={String(p.id)}>
                        {p.title}
                        {p.id === propertyId
                          ? ` · ${t("admin.reassign.currentLower", "current")}`
                          : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex flex-col gap-1 sm:items-end">
                <label className="flex items-center gap-2 text-xs text-muted-foreground select-none cursor-pointer">
                  <Switch
                    checked={allowDisplacement}
                    onCheckedChange={setAllowDisplacement}
                  />
                  {t(
                    "admin.reassign.allowDisplacement",
                    "Allow displacement chains"
                  )}
                </label>
                <p className="text-[11px] leading-snug text-muted-foreground/80 max-w-xs sm:text-right">
                  {allowDisplacement
                    ? t(
                        "admin.reassign.displacementOnHint",
                        "You can move this tenant into an occupied room. The person there gets bumped and you'll pick their new room next — repeating until everyone lands somewhere. All moves commit together."
                      )
                    : t(
                        "admin.reassign.displacementOffHint",
                        "Only empty rooms can be picked. Turn on to move this tenant into an occupied room (the current occupant will be reassigned in a chain)."
                      )}
                </p>
              </div>
            </div>

            {isLoading ? (
              <div className="space-y-2">
                {Array.from({ length: 4 }).map((_, i) => (
                  <Skeleton key={i} className="h-14 w-full" />
                ))}
              </div>
            ) : rowsByOccupancy.length === 0 ? (
              <Card className="p-4 text-sm text-muted-foreground">
                {propertyFilter === "all"
                  ? t("admin.reassign.noRoomsFound", "No rooms found.")
                  : t(
                      "admin.reassign.noRoomsInProperty",
                      "No rooms in the selected property. Switch to All properties to see other options."
                    )}
              </Card>
            ) : (
              <ul className="space-y-1.5">
                {rowsByOccupancy.map(
                  ({
                    room,
                    occupant,
                    treatAsVacant,
                    earmarked,
                    isCurrent,
                    propertyTitle,
                  }) => {
                    // Occupied rooms are locked unless the admin opts
                    // into displacement chains via the toggle above.
                    // Earlier-displaced occupants (treatAsVacant=true)
                    // are still pickable since their old room is
                    // effectively vacant for the rest of this chain.
                    const blockedByDisplacementGate =
                      !allowDisplacement && !treatAsVacant;
                    const disabled =
                      isCurrent || earmarked || blockedByDisplacementGate;
                    return (
                      <li key={room.id}>
                        <button
                          type="button"
                          disabled={disabled || commitMut.isPending}
                          onClick={() => pickRoom(room)}
                          className={cn(
                            "w-full text-left rounded-md border px-3 py-2.5 transition-colors",
                            "flex items-center justify-between gap-3",
                            "hover:border-primary hover:bg-primary/5",
                            "disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:border-border disabled:hover:bg-transparent",
                            isCurrent && "border-primary/40 bg-primary/5"
                          )}
                        >
                          <div className="min-w-0 flex-1">
                            <div className="text-sm font-medium flex items-center gap-2 flex-wrap">
                              {room.title}
                              {room.price != null && (
                                <span className="text-xs font-normal text-muted-foreground">
                                  {formatRentPerMonth(room.price)}
                                </span>
                              )}
                            </div>
                            <div className="text-[11px] text-muted-foreground/80 mt-0.5">
                              {propertyTitle}
                              {room.property_id !== propertyId && (
                                <span className="ml-1.5 inline-flex items-center rounded-sm bg-amber-100 text-amber-900 px-1 py-0 text-[10px] font-medium align-middle">
                                  {t(
                                    "admin.reassign.crossProperty",
                                    "cross-property"
                                  )}
                                </span>
                              )}
                            </div>
                            <div className="text-xs text-muted-foreground mt-0.5 flex items-center gap-2 flex-wrap">
                              {isCurrent && (
                                <Badge
                                  variant="outline"
                                  className="font-normal"
                                >
                                  {t(
                                    "admin.reassign.currentRoom",
                                    "Current room"
                                  )}
                                </Badge>
                              )}
                              {earmarked && (
                                <Badge
                                  variant="outline"
                                  className="font-normal"
                                >
                                  {t(
                                    "admin.reassign.alreadyAssigned",
                                    "Already assigned in this chain"
                                  )}
                                </Badge>
                              )}
                              {!isCurrent &&
                                !earmarked &&
                                (treatAsVacant ? (
                                  <span className="text-emerald-600">
                                    {t("admin.reassign.vacant", "Vacant")}
                                  </span>
                                ) : (
                                  <span className="flex items-center gap-1">
                                    <Users className="h-3 w-3" />
                                    {t(
                                      "admin.reassign.occupied",
                                      "Occupied"
                                    )} · {nameForOccupant(occupant!, t)}
                                    {blockedByDisplacementGate && (
                                      <span
                                        className="ml-1 text-[10px] uppercase tracking-wide text-muted-foreground/80"
                                        title={t(
                                          "admin.reassign.lockedTitle",
                                          "Enable 'Allow displacement chains' above to pick this room"
                                        )}
                                      >
                                        · {t("admin.reassign.locked", "locked")}
                                      </span>
                                    )}
                                  </span>
                                ))}
                            </div>
                          </div>
                          {!disabled && (
                            <ArrowRight className="h-4 w-4 text-muted-foreground shrink-0" />
                          )}
                        </button>
                      </li>
                    );
                  }
                )}
              </ul>
            )}
          </div>
        )}

        <DialogFooter className="gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={commitMut.isPending}
          >
            <X className="mr-2 h-4 w-4" />
            {t("admin.notes.cancel", "Cancel")}
          </Button>
          <Button
            type="button"
            onClick={handleCommit}
            disabled={!canCommit || commitMut.isPending}
          >
            {commitMut.isPending ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Check className="mr-2 h-4 w-4" />
            )}
            {t("admin.reassign.commitPrefix", "Commit")} {resolved.length || ""}{" "}
            {resolved.length === 1
              ? t("admin.reassign.moveSingular", "move")
              : t("admin.reassign.movesPlural", "moves")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
